<template>
  <div>
    <div v-if="showOptionsMenu" class="ffi-backdrop" @click="showOptionsMenu = false"></div>

    <!-- Grid Mode -->
    <div v-if="!listMode && (file || folder)" class="ffi-card" :class="[{ 'ffi-file': !!file }, { 'ffi-folder': !!folder }]" @dblclick="handleFileClick(file || folder)">
      <div class="ffi-card-icon">
        <i :class="['fas', file ? 'fa-file-alt' : 'fa-folder']"></i>
      </div>
      <div class="ffi-card-name" :title="fullName">{{ fileFolderName }}</div>
      <div class="ffi-card-meta" v-if="file">{{ formatFileSize(file.size) }}</div>
      <div class="ffi-card-meta" v-else>Folder</div>
      <div class="ffi-menu-trigger" @click.stop="showOptionsMenu = !showOptionsMenu">
        <i class="fas fa-ellipsis-v"></i>
      </div>
      <div class="ffi-menu" v-if="showOptionsMenu" @click.stop="showOptionsMenu = false">
        <div class="ffi-menu-item" v-if="file" @click="showShareModal = true"><i class="fas fa-share-alt"></i> Share</div>
        <div class="ffi-menu-item" @click="showUpdateModal = true"><i class="fas fa-pen"></i> Rename</div>
        <div class="ffi-menu-item" @click="openInfo"><i class="fas fa-info-circle"></i> Info</div>
        <div class="ffi-menu-item" v-if="file" @click="downloadItem"><i class="fas fa-download"></i> Download</div>
        <div class="ffi-menu-item ffi-menu-danger" @click="deleteFileFolder"><i class="fas fa-trash-alt"></i> Delete</div>
      </div>
    </div>

    <!-- List Mode -->
    <div v-if="listMode && (file || folder)" class="ffi-row" @dblclick="handleFileClick(file || folder)">
      <div class="ffi-row-name">
        <i :class="['ffi-row-icon fas', file ? 'fa-file-alt ffi-ic-file' : 'fa-folder ffi-ic-folder']"></i>
        <span :title="fullName">{{ fileFolderName }}</span>
      </div>
      <div class="ffi-row-date">{{ formatIndianDate(file ? file.createdAt : folder.createdAt) }}</div>
      <div class="ffi-row-size">{{ file ? formatFileSize(file.size) : '—' }}</div>
      <div class="ffi-row-act" @click.stop="showOptionsMenu = !showOptionsMenu">
        <i class="fas fa-ellipsis-v"></i>
        <div class="ffi-menu ffi-menu-right" v-if="showOptionsMenu" @click.stop="showOptionsMenu = false">
          <div class="ffi-menu-item" v-if="file" @click="showShareModal = true"><i class="fas fa-share-alt"></i> Share</div>
          <div class="ffi-menu-item" @click="showUpdateModal = true"><i class="fas fa-pen"></i> Rename</div>
          <div class="ffi-menu-item" @click="openInfo"><i class="fas fa-info-circle"></i> Info</div>
          <div class="ffi-menu-item" v-if="file" @click="downloadItem"><i class="fas fa-download"></i> Download</div>
          <div class="ffi-menu-item ffi-menu-danger" @click="deleteFileFolder"><i class="fas fa-trash-alt"></i> Delete</div>
        </div>
      </div>
    </div>

    <!-- Rename Modal -->
    <CustomModal v-if="showUpdateModal" :title="`Rename ${file ? 'File' : 'Folder'}`" :show="showUpdateModal" @close="showUpdateModal = false">
      <div class="ffi-modal-body">
        <div class="ffi-modal-field">
          <label class="ffi-label">Name</label>
          <input class="ffi-input" placeholder="Enter name" v-model="newName" type="text" />
        </div>
        <button class="ffi-submit-btn" @click="updateFileFolder" :disabled="processing">
          <i :class="processing ? 'fas fa-spinner fa-spin' : 'fas fa-check'"></i>
          {{ processing ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </CustomModal>

    <!-- Share Modal -->
    <CustomModal v-if="showShareModal" :title="`Share ${file ? 'File' : 'Folder'}`" :show="showShareModal" @close="showShareModal = false">
      <div class="my-3">
        <ShareFile @close="showShareModal = false" :isFolder="!!folder" :fileFolder="file || folder" />
      </div>
    </CustomModal>
  </div>
</template>

<script>
import { formatIndianDate, stripText } from "../../utils/common";
import { formatFileSize } from "../../utils/file-utils";
import { http, serverURL } from "../../utils/http";
import CustomModal from "../layout/CustomModal.vue";
import ShareFile from "./ShareFile.vue";

export default {
  emits: ["openFolder", "openInfo"],
  props: {
    folder: { type: Object, default: null },
    listMode: { type: Boolean, default: false },
    file: { type: Object, default: null },
  },
  components: { ShareFile, CustomModal },
  data() {
    return {
      showShareModal: false,
      showUpdateModal: false,
      processing: false,
      newName: "",
      showOptionsMenu: false,
    };
  },
  computed: {
    fullName() {
      return (this.file && this.file.name) || (this.folder && this.folder.name) || '';
    },
    fileFolderName() {
      return stripText(this.fullName, this.listMode ? 200 : 22);
    },
  },
  methods: {
    formatFileSize,
    formatIndianDate,
    handleFileClick(fileFolderData) {
      if (fileFolderData.src) { this.downloadItem(); } else { this.$emit("openFolder"); }
    },
    downloadItem() {
      window.open(serverURL + "/" + this.file.src, "_blank");
      this.showOptionsMenu = false;
    },
    async deleteFileFolder() {
      try {
        if (!confirm("Are you sure?")) return;
        this.processing = true;
        await http.delete(`/file-management/${this.file ? "files" : "folders"}/${this.file ? this.file.id : this.folder.id}`);
        this.$toast.success(`${this.file ? "File" : "Folder"} deleted successfully`);
        this.showUpdateModal = false;
        window.location.reload();
      } catch (err) { console.log("error in deleting file/folder", err); this.$toast.error("Could not delete!"); }
      this.processing = false;
    },
    async updateFileFolder() {
      try {
        this.processing = true;
        await http.patch(`/file-management/${this.file ? "files" : "folders"}/${this.file ? this.file.id : this.folder.id}`, { name: this.newName });
        this.$toast.success(`${this.file ? "File" : "Folder"} updated successfully`);
        this.showUpdateModal = false;
        window.location.reload();
      } catch (err) { console.log("error in updating file/folder", err); this.$toast.error("Could not update!"); }
      this.processing = false;
    },
    openInfo() {
      this.$emit("openInfo", this.file || this.folder);
      this.showOptionsMenu = false;
    },
  },
  mounted() {
    this.newName = this.file ? this.file.name : this.folder ? this.folder.name : '';
  },
};
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

/* Grid Card */
.ffi-card { position:relative; background:white; border:1px solid $gray100; border-radius:10px; padding:16px 14px; text-align:center; cursor:pointer; transition:all $transitionFast; &:hover { box-shadow:$shadowMd; transform:translateY(-1px); } }
.ffi-card.ffi-folder .ffi-card-icon { color:#f59e0b; background:#fffbeb; }
.ffi-card.ffi-file .ffi-card-icon { color:$primaryColor; background:$primaryLight; }
.ffi-card-icon { width:44px; height:44px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:18px; margin:0 auto 8px; }
.ffi-card-name { font-size:12.5px; font-weight:600; color:$gray800; line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ffi-card-meta { font-size:11px; color:$gray400; font-weight:500; margin-top:2px; }
.ffi-menu-trigger { position:absolute; top:8px; right:8px; width:24px; height:24px; border-radius:4px; display:flex; align-items:center; justify-content:center; color:$gray400; font-size:12px; transition:all $transitionFast; &:hover { background:$gray100; color:$gray600; } }

/* Context Menu */
.ffi-menu { position:absolute; top:32px; right:4px; z-index:200; background:white; border:1px solid $gray100; border-radius:8px; box-shadow:$shadowLg; min-width:140px; padding:4px; overflow:hidden; }
.ffi-menu-right { right:0; top:24px; }
.ffi-menu-item { display:flex; align-items:center; gap:8px; padding:7px 10px; font-size:12px; font-weight:500; color:$gray600; border-radius:5px; cursor:pointer; transition:all $transitionFast; i { width:14px; text-align:center; font-size:11px; } &:hover { background:$gray50; color:$gray800; } }
.ffi-menu-danger { color:#dc2626; &:hover { background:#fee2e2; color:#dc2626; } }

/* List Row */
.ffi-row { display:grid; grid-template-columns:1fr 120px 100px 40px; align-items:center; padding:9px 14px; cursor:pointer; transition:background $transitionFast; position:relative; border-bottom:1px solid $gray50; &:hover { background:$gray50; } &:last-child { border-bottom:none; } }
.ffi-row-name { display:flex; align-items:center; gap:8px; font-size:13px; font-weight:600; color:$gray800; min-width:0; span { white-space:nowrap; overflow:hidden; text-overflow:ellipsis; } }
.ffi-row-icon { font-size:15px; flex-shrink:0; }
.ffi-ic-folder { color:#f59e0b; }
.ffi-ic-file { color:$primaryColor; }
.ffi-row-date { font-size:12px; color:$gray500; text-align:right; }
.ffi-row-size { font-size:12px; color:$gray500; text-align:right; }
.ffi-row-act { text-align:right; position:relative; color:$gray400; font-size:12px; cursor:pointer; padding:4px; &:hover { color:$gray600; } }

/* Backdrop */
.ffi-backdrop { position:fixed; left:0; top:0; width:100vw; height:100vh; z-index:100; }

/* Modal */
.ffi-modal-body { padding:8px 0; display:flex; flex-direction:column; gap:12px; }
.ffi-modal-field { display:flex; flex-direction:column; gap:4px; }
.ffi-label { font-size:12px; font-weight:600; color:$gray600; }
.ffi-input { width:100%; padding:8px 12px; border:1px solid $gray200; border-radius:6px; font-size:13px; font-weight:500; color:$gray800; font-family:inherit; outline:none; transition:all $transitionFast; &:focus { border-color:$primaryColor; box-shadow:0 0 0 3px rgba(0,94,137,.08); } &::placeholder { color:$gray300; } }
.ffi-submit-btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; width:100%; padding:8px; border:none; border-radius:6px; background:$primaryColor; color:white; font-size:12.5px; font-weight:600; cursor:pointer; font-family:inherit; transition:all $transitionFast; &:hover { background:$primaryDark; } &:disabled { opacity:0.6; cursor:not-allowed; } }
</style>
